import axiosLib from "axios";

// Client for the LavaFloor launcher (floor-control-v2/launcher.py) that runs
// on the floor's Windows machine. Two ways to reach it, both configurable
// from the Lava Floor page (gear icon), persisted per device in localStorage:
//
//  * REMOTE (default setup): the launcher's token-protected API (port 8001)
//    published on the internet with Tailscale Funnel, e.g.
//    https://floor-pc.tailxxxx.ts.net — works from any network (phone on
//    cellular included). Needs the token from remote_token.txt on the PC.
//  * LAN: http://<pc-ip>:8000 on the same WiFi, no token. Only works when
//    this app itself is served over plain HTTP (browsers block HTTPS pages
//    from calling HTTP addresses — "mixed content").

const URL_KEY = "lavaFloorUrl";
const TOKEN_KEY = "lavaFloorToken";
const DEFAULT_URL = "http://192.168.1.100:8000";

export const FLOOR_GAME_ID = "time_trial";

export const getFloorUrl = () => localStorage.getItem(URL_KEY) || DEFAULT_URL;

export const setFloorUrl = (url: string) =>
  localStorage.setItem(URL_KEY, url.trim().replace(/\/+$/, ""));

export const getFloorToken = () => localStorage.getItem(TOKEN_KEY) || "";

export const setFloorToken = (token: string) => {
  const t = token.trim();
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

// true when the launcher answered 401 — wrong/missing token, not "offline"
export const isFloorAuthError = (e: unknown): boolean =>
  !!(e as { response?: { status?: number } })?.response &&
  (e as { response: { status?: number } }).response.status === 401;

export type FloorLastResult = {
  game: string;
  score: number; // Time Trial: final time in seconds (elapsed + penalties)
  won: boolean | null;
  stats: Record<string, string | number> | null;
  tag: string | null; // echo of the tag we sent with /api/start
};

export type FloorStatus = {
  game: string | null;
  selector: boolean;
  state: "idle" | "countdown" | "running" | "game_over" | null;
  sim: boolean;
  version: string;
  score: number | null;
  lives: number | null;
  tag: string | null;
  stats?: Record<string, string | number>;
  last_result: FloorLastResult | null;
  error: string | null;
};

export type FloorGame = {
  id: string;
  name: string;
  description: string;
  lower_is_better: boolean;
  levels: string[];
  broken: boolean;
};

const client = () => {
  const token = getFloorToken();
  return axiosLib.create({
    baseURL: getFloorUrl(),
    timeout: 4000,
    headers: token ? { "X-Floor-Token": token } : {},
  });
};

export const GetFloorGames = (): Promise<FloorGame[]> =>
  client()
    .get("/api/games")
    .then((res) => res.data);

export const GetFloorStatus = (): Promise<FloorStatus> =>
  client()
    .get("/api/status")
    .then((res) => res.data);

// Starts a Time Trial run and skips the on-floor button: the floor goes
// straight to the 3-2-1 countdown. `tag` comes back in last_result so we
// know THIS run (and not an older one) has finished.
export const StartFloorRun = (level: string | null, tag: string) =>
  client()
    .post(`/api/start/${FLOOR_GAME_ID}`, { level, go: true, tag })
    .then((res) => res.data);

export const StopFloor = () =>
  client()
    .post("/api/stop")
    .then((res) => res.data);
